/**
 * AppointmentsListPage
 * Main page for listing and managing appointments
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  Cancel,
  CheckCircle,
  Done,
  Refresh,
  Description,
  Science,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppointments } from '../../hooks';
import { PageShell, DataTable, HelpButton, type DataTableColumn, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { InlineFilterBar, InlineSelect, InlineMultiSelect } from '../../components/common';
import type { ActiveChip } from '../../hooks/useFilterState';
import {
  AppointmentResponse,
  AppointmentStatus,
  AppointmentExamType,
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
} from '../../types/appointment';
import appointmentService from '../../services/appointmentService';

/**
 * Status badge component
 */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const color = APPOINTMENT_STATUS_COLORS[status as AppointmentStatus] || 'default';
  const label = APPOINTMENT_STATUS_LABELS[status as AppointmentStatus] || status;

  return <Chip label={label} color={color} size="small" />;
};

const EXAM_TYPE_OPTIONS = Object.values(AppointmentExamType).map((type) => ({
  value: type,
  label: type,
}));

const STATUS_OPTIONS = Object.values(AppointmentStatus).map((status) => ({
  value: status,
  label: APPOINTMENT_STATUS_LABELS[status],
}));

const columns: DataTableColumn<AppointmentResponse>[] = [
  {
    id: 'date',
    label: 'Data / Horário',
    render: (appointment) => (
      <Box sx={{ whiteSpace: 'nowrap' }}>
        {appointmentService.formatDate(appointment.selectedDate)}
        {appointmentService.formatTime(appointment.hour, appointment.minute) ? ' - ' : ''}
        <Typography component="span" variant="body2" color="text.secondary">
          {appointmentService.formatTime(appointment.hour, appointment.minute) || ''}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'employee',
    label: 'Funcionário',
    primary: true,
    render: (appointment) => (
      <>
        <Typography variant="body2" fontWeight={500}>
          {appointment.employeeName}
        </Typography>
        {appointment.employeeCpf && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {appointment.employeeCpf}
          </Typography>
        )}
      </>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (appointment) => (
      <Typography
        variant="body2"
        sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {appointment.companyName}
      </Typography>
    ),
  },
  {
    id: 'clinic',
    label: 'Clínica / Médico',
    render: (appointment) =>
      appointment.clinicName ? (
        <>
          <Typography variant="body2">{appointment.clinicName}</Typography>
          {appointment.doctorName && (
            <Typography variant="caption" color="text.secondary">
              {appointment.doctorName}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      ),
  },
  {
    id: 'examType',
    label: 'Tipo de Exame',
    render: (appointment) => (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2">{appointment.examType}</Typography>
        {appointment.appointmentExams && appointment.appointmentExams.length > 0 && (
          <Tooltip
            title={appointment.appointmentExams
              .map((e) => e.occupationalExam?.examName ?? `Exame #${e.occupationalExamId}`)
              .join(', ')}
          >
            <Chip
              icon={<Science sx={{ fontSize: 14 }} />}
              label={appointment.appointmentExams.length}
              size="small"
              variant="outlined"
              color="info"
              sx={{ height: 22, '& .MuiChip-label': { px: 0.5 } }}
            />
          </Tooltip>
        )}
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (appointment) => <StatusBadge status={appointment.status} />,
  },
];

export const AppointmentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/appointments/:id/edit', pode('appointment.update'), 'Editar');
  const visualizar = useControle('/appointments/:id', pode('appointment.read'), 'Visualizar');
  const confirmar = useAcao('appointment.update', 'Confirmar');
  const concluir = useAcao('appointment.update', 'Concluir');
  const laudosTerceirizados = useControle(
    '/appointments/:id/referral-reports',
    pode('referral_report.manage'),
    'Laudos Terceirizados'
  );
  const cancelar = useAcao('appointment.update', 'Cancelar');

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<AppointmentResponse | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<AppointmentStatus[]>([]);
  const [pendingApprovalCount, setPendingApprovalCount] = useState<number>(0);

  useEffect(() => {
    appointmentService.getPendingApprovalCount()
      .then(count => setPendingApprovalCount(count))
      .catch(() => setPendingApprovalCount(0));
  }, []);

  // Hooks
  const {
    appointments,
    loading,
    pagination,
    filters,
    setPage,
    setLimit,
    setFilters,
    refresh,
    cancelAppointment,
    confirmAppointment,
    completeAppointment,
  } = useAppointments();

  // Auto-apply status filter when the selection changes (empty = all statuses)
  useEffect(() => {
    setFilters({
      ...filters,
      status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    });
  }, [selectedStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const handleView = (appointment: AppointmentResponse) => {
    navigate(`/appointments/${appointment.id}`);
  };

  const handleEdit = (appointment: AppointmentResponse) => {
    navigate(`/appointments/${appointment.id}/edit`);
  };

  const handleCancelClick = (appointment: AppointmentResponse) => {
    setAppointmentToCancel(appointment);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (appointmentToCancel) {
      const success = await cancelAppointment(appointmentToCancel.id);
      if (success) {
        setCancelDialogOpen(false);
        setAppointmentToCancel(null);
      }
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setAppointmentToCancel(null);
  };

  const handleConfirm = async (appointment: AppointmentResponse) => {
    await confirmAppointment(appointment.id);
  };

  const handleComplete = async (appointment: AppointmentResponse) => {
    await completeAppointment(appointment.id);
  };

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value || undefined });
  };

  const handleExamTypeChange = (value: string) => {
    setFilters({ ...filters, examType: value || undefined });
  };

  const handleClearFilters = () => {
    setSelectedStatuses([]);
    setFilters({});
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage);
  };

  const canCancel = (status: string) => appointmentService.canCancel(status);
  const canEdit = (status: string) => appointmentService.canEdit(status);

  const examTypeValue = Array.isArray(filters.examType)
    ? filters.examType[0] ?? ''
    : filters.examType ?? '';

  const filterChips: ActiveChip[] = [];
  if (examTypeValue) {
    filterChips.push({
      key: 'examType',
      label: 'Tipo de Exame',
      value: examTypeValue,
      onDelete: () => handleExamTypeChange(''),
    });
  }
  selectedStatuses.forEach((status) => {
    filterChips.push({
      key: `status-${status}`,
      label: 'Status',
      value: APPOINTMENT_STATUS_LABELS[status],
      onDelete: () => setSelectedStatuses((prev) => prev.filter((s) => s !== status)),
    });
  });

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Agendamentos' },
      ]}
      title="Gestão de Agendamentos"
      subtitle="Gerencie os agendamentos de exames ocupacionais"
      actions={
        <>
          <HelpButton entity="appointments" screen="lista" size="small" />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refresh}
          >
            Atualizar
          </Button>
          <RouteActionButton
            para="/appointments/new"
            exige={pode('appointment.create')}
            variant="contained"
            startIcon={<Add />}
          >
            Novo Agendamento
          </RouteActionButton>
        </>
      }
    >
        {/* Pending Approval Warning */}
        {pendingApprovalCount > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/appointments/pending')}>
                Ver pendentes
              </Button>
            }
          >
            {pendingApprovalCount} agendamento{pendingApprovalCount > 1 ? 's' : ''} aguardando aprovação da clínica
          </Alert>
        )}

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <InlineFilterBar
            searchPlaceholder="Buscar por funcionário..."
            searchValue={filters.search ?? ''}
            onSearchChange={handleSearchChange}
            resultCount={pagination.total}
            resultNoun="agendamentos"
            chips={filterChips}
            onClearAll={handleClearFilters}
            loading={loading}
          >
            <InlineSelect
              label="Tipo de Exame"
              value={examTypeValue}
              options={EXAM_TYPE_OPTIONS}
              onChange={handleExamTypeChange}
              emptyLabel="Todos"
              minWidth={180}
            />
            <InlineMultiSelect
              label="Status"
              value={selectedStatuses}
              options={STATUS_OPTIONS}
              onChange={(v) => setSelectedStatuses(v as AppointmentStatus[])}
              minWidth={150}
            />
          </InlineFilterBar>
        </Box>

        {/* Appointments Table */}
        <DataTable
          columns={columns}
          rows={appointments}
          getRowId={(appointment) => appointment.id}
          loading={loading}
          emptyState={{
            title: 'Nenhum agendamento ainda',
            description:
              'Agendamentos marcam os exames de um funcionário em um horário disponível de um profissional.',
            prereqChain: 'appointments',
            action: {
              label: 'Novo agendamento',
              node: 'appointments',
              deniedHint: 'Peça ao administrador da sua clínica para criar um agendamento.',
            },
          }}
          filtersActive={
            selectedStatuses.length > 0 || Boolean(filters.search) || examTypeValue !== ''
          }
          onClearFilters={handleClearFilters}
          actions={[
            {
              icon: <Visibility fontSize="small" />,
              color: 'primary',
              onClick: handleView,
              ...visualizar,
            },
            {
              icon: <Edit fontSize="small" />,
              color: 'primary',
              onClick: handleEdit,
              hidden: (appointment) => !canEdit(appointment.status),
              ...editar,
            },
            {
              icon: <CheckCircle fontSize="small" />,
              color: 'success',
              onClick: handleConfirm,
              hidden: (appointment) => appointment.status !== AppointmentStatus.PENDENTE,
              ...confirmar,
            },
            {
              icon: <Done fontSize="small" />,
              color: 'success',
              onClick: handleComplete,
              hidden: (appointment) => appointment.status !== AppointmentStatus.CONFIRMADO,
              ...concluir,
            },
            {
              icon: <Description fontSize="small" />,
              color: 'warning',
              onClick: (appointment) =>
                navigate(`/appointments/${appointment.id}/referral-reports`),
              hidden: (appointment) => appointment.status !== AppointmentStatus.AGUARDANDO_LAUDOS,
              ...laudosTerceirizados,
            },
            {
              icon: <Cancel fontSize="small" />,
              color: 'error',
              onClick: handleCancelClick,
              hidden: (appointment) => !canCancel(appointment.status),
              ...cancelar,
            },
          ]}
          pagination={{
            page: pagination.page - 1,
            rowsPerPage: pagination.limit,
            totalCount: pagination.total,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
            rowsPerPageOptions: [10, 20, 50, 100],
          }}
        />

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onClose={handleCancelDialogClose}>
          <DialogTitle>Confirmar Cancelamento</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja cancelar o agendamento de{' '}
              <strong>{appointmentToCancel?.employeeName}</strong> para{' '}
              <strong>{appointmentToCancel?.examType}</strong>?
              <br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialogClose}>Voltar</Button>
            <Button onClick={handleCancelConfirm} color="error" variant="contained">
              Cancelar Agendamento
            </Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default AppointmentsListPage;
